import classnames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'validate.js';

import ClampedMultiLineText from 'dpl/common/components/ClampedMultiLineText';
import BreederProfileMeetBreederSectionSkeleton from 'dpl/components/BreederProfile/Show/skeletons/BreederProfileMeetBreederSectionSkeleton';
import SmartImage from 'dpl/components/SmartImage';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import {
  aboutBreederResourceDefinition,
  breederInterviewResourceDefinition
} from 'dpl/shared/utils/tptConnectResourceDefinitions';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import TruncatedText from 'dpl/components/BreederProfile/mobile/components/about/TruncatedText';

import meetBreederPlaceholder from 'dpl/assets/breeder_profile/meet_breeder_placeholder.svg';

const IS_LT_MD = isLessThanBreakpoint('md');
const CLAMPED_TEXT_LINE_HEIGHT = IS_LT_MD ? 21.25 : 24;

interface MeetBreederImageProps {
  className?: string;
  photoUrl?: string | null;
}

function MeetBreederImage({
  className,
  photoUrl = null
}: MeetBreederImageProps) {
  return (
    <div className={className}>
      {photoUrl ? (
        <div className="aspect-ratio aspect-ratio--6x4 relative">
          <div className="aspect-ratio--object">
            <SmartImage
              Tag="div"
              className="w-100 h-100 cover bg-center"
              wrapperOnlyClassName="br3 overflow-hidden"
              src={photoUrl}
              lazy
              crop
            />
          </div>
        </div>
      ) : (
        <div className={className}>
          <SmartImage src={meetBreederPlaceholder} />
        </div>
      )}
    </div>
  );
}

interface BreederProfileMeetBreederSectionContainerProps {
  className?: string | null;
}

export default function BreederProfileMeetBreederSectionContainer({
  className = null
}: BreederProfileMeetBreederSectionContainerProps) {
  const { breederProfileId, introParagraph, breederFirstName } = useSelector(
    ({ server }: any) => server
  );

  const {
    value: { data: interview },
    isFetching: isFetchingInterview
  } = useResourceQuery(breederInterviewResourceDefinition(breederProfileId));

  const {
    isFetching: isFetchingQuestions,
    value: {
      data: { questions }
    }
  } = useResourceQuery(aboutBreederResourceDefinition(breederProfileId));

  if (isFetchingInterview || isFetchingQuestions) {
    return <BreederProfileMeetBreederSectionSkeleton />;
  }

  return (
    <div
      className={classnames(
        'BreederProfileMeetBreederSectionContainer container-xxl mv8 mv20-md',
        className
      )}
    >
      <div className="flex flex-column flex-md-row gap-x-96">
        <div className="w-50-md">
          <h2 className="font-28 md:font-40 lh-title mb5 mb6-md">
            Meet the breeder
          </h2>
          <ClampedMultiLineText
            className="font-16 md:font-18 secondary mb8 mb14-md"
            text={introParagraph}
            numOfLines={6}
            lineHeight={CLAMPED_TEXT_LINE_HEIGHT}
            moreText="Read more"
            lessText="Show less"
            fadeToButton
            isOpen={window.__DPL_BOT_UA}
          />
          <MeetBreederImage className="dn-ns" photoUrl={interview.photo?.url} />
          {!isEmpty(questions) && (
            <div className="mt8">
              <h3 className="font-serif font-24 lh-title mb4 mb6-md">
                Q. & A. with {breederFirstName}
              </h3>
              <ul className="flex flex-column gap-32">
                {questions.map(({ question, answer }: any, index: number) => (
                  <li key={index}>
                    <div>
                      <p className="font-16 md:font-18 fw-medium lh-title mb3">
                        {question}
                      </p>
                      <TruncatedText
                        className="font-16 md:font-18 secondary"
                        text={answer}
                        lineHeight={1.4}
                        maxLines={4}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="dn db-ns w-50-md">
          <MeetBreederImage photoUrl={interview.photo?.url} />
        </div>
      </div>
    </div>
  );
}
